import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FolderKanban, UserPlus, FolderPlus, Sparkles, X, Check, Loader2, Info } from "lucide-react";

const SEL = "w-full bg-navy border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none";
const LBL = "block text-xs uppercase tracking-wide text-gray-500 mb-1.5";
const NEW = "__new__";

const MODES = [
  { id: "existing", label: "Use Existing Project", icon: FolderKanban },
  { id: "create", label: "Create New Project", icon: FolderPlus },
  { id: "none", label: "Generate Without Project", icon: Sparkles },
];

export function ClientProjectPicker({ clients, projects, clientId, setClientId, projectId, setProjectId, reloadClients, reloadProjects }) {
  const [mode, setMode] = useState("existing");
  const [showNewClient, setShowNewClient] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [ncName, setNcName] = useState(""); const [ncEmail, setNcEmail] = useState("");
  const [npName, setNpName] = useState("");
  const [cpName, setCpName] = useState(""); // create-mode project name
  const [busy, setBusy] = useState(false);

  const pickMode = (m) => {
    setMode(m); setShowNewClient(false); setShowNewProject(false);
    if (m === "none") setProjectId("");
    if (m === "create") setProjectId("");
  };

  const createClient = async () => {
    if (!ncName.trim()) { toast.error("Client name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/customers", { name: ncName.trim(), email: ncEmail.trim() || null });
      await reloadClients();
      setClientId(data.user_id);
      setShowNewClient(false); setNcName(""); setNcEmail("");
      toast.success(`Client “${data.name}” added`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const createProject = async (name, closeFn) => {
    if (!name.trim()) { toast.error("Project name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/projects", { org_name: name.trim(), client_id: clientId || null });
      await reloadProjects();
      setProjectId(data.id);
      closeFn && closeFn();
      toast.success(`Project “${data.org_name}” created — content will auto-save here`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const ClientSelect = (
    <div>
      <label className={LBL}>Client (optional)</label>
      <select className={SEL} value={clientId} data-testid="cpp-client"
        onChange={(e) => { const v = e.target.value; if (v === NEW) { setShowNewClient(true); } else { setClientId(v); } }}>
        <option value="">— none —</option>
        <option value={NEW}>➕ New client…</option>
        {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>)}
      </select>
      {showNewClient && (
        <div className="mt-2 p-3 border border-gold/25 bg-navy space-y-2" data-testid="cpp-new-client-form">
          <div className="flex items-center gap-2 text-xs text-gold"><UserPlus size={13} /> New client</div>
          <input className={SEL} placeholder="Client / business name *" value={ncName} onChange={(e) => setNcName(e.target.value)} data-testid="cpp-new-client-name" />
          <input className={SEL} placeholder="Email (optional)" value={ncEmail} onChange={(e) => setNcEmail(e.target.value)} data-testid="cpp-new-client-email" />
          <div className="flex gap-2">
            <button onClick={createClient} disabled={busy} data-testid="cpp-new-client-save" className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gold text-navy font-medium disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Add client</button>
            <button onClick={() => { setShowNewClient(false); setNcName(""); setNcEmail(""); }} data-testid="cpp-new-client-cancel" className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-300"><X size={13} /> Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="client-project-picker">
      {/* Startup choice */}
      <div className="grid grid-cols-3 gap-2" data-testid="cpp-mode">
        {MODES.map((m) => (
          <button key={m.id} onClick={() => pickMode(m.id)} data-testid={`cpp-mode-${m.id}`}
            className={`flex items-center gap-2 px-3 py-2.5 border text-left text-xs sm:text-sm transition-colors ${mode === m.id ? "border-gold/60 bg-gold/10 text-white" : "border-white/10 bg-navy text-gray-400 hover:border-white/25"}`}>
            <m.icon size={15} className={mode === m.id ? "text-gold shrink-0" : "shrink-0"} /> <span className="truncate">{m.label}</span>
          </button>
        ))}
      </div>

      {mode === "existing" && (
        <div className="grid md:grid-cols-2 gap-4">
          {ClientSelect}
          <div>
            <label className={LBL}>Project (auto-saves here)</label>
            <select className={SEL} value={projectId} data-testid="cpp-project"
              onChange={(e) => { const v = e.target.value; if (v === NEW) { setShowNewProject(true); } else { setProjectId(v); } }}>
              <option value="">— none —</option>
              <option value={NEW}>➕ New project…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.org_name}</option>)}
            </select>
            {showNewProject && (
              <div className="mt-2 p-3 border border-gold/25 bg-navy space-y-2" data-testid="cpp-new-project-form">
                <div className="flex items-center gap-2 text-xs text-gold"><FolderPlus size={13} /> New project{clientId ? " for selected client" : ""}</div>
                <input className={SEL} placeholder="Project name *" value={npName} onChange={(e) => setNpName(e.target.value)} data-testid="cpp-new-project-name" />
                <div className="flex gap-2">
                  <button onClick={() => createProject(npName, () => { setShowNewProject(false); setNpName(""); })} disabled={busy} data-testid="cpp-new-project-save" className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gold text-navy font-medium disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Create project</button>
                  <button onClick={() => { setShowNewProject(false); setNpName(""); }} data-testid="cpp-new-project-cancel" className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-300"><X size={13} /> Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === "create" && (
        <div className="grid md:grid-cols-2 gap-4">
          {ClientSelect}
          <div>
            <label className={LBL}>New project name</label>
            {projectId ? (
              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 px-3 py-2.5 text-emerald-300 text-sm" data-testid="cpp-create-done">
                <Check size={15} /> Project ready — content will auto-save here.
              </div>
            ) : (
              <div className="flex gap-2">
                <input className={SEL} placeholder="e.g. Montgomery Plumbing Website" value={cpName} onChange={(e) => setCpName(e.target.value)} data-testid="cpp-create-project-name" />
                <button onClick={() => createProject(cpName, () => setCpName(""))} disabled={busy} data-testid="cpp-create-project-btn" className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs bg-gold text-navy font-medium disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={13} /> : <FolderPlus size={13} />} Create</button>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === "none" && (
        <div className="flex items-start gap-2 bg-navy border border-white/10 px-4 py-3 text-gray-400 text-sm" data-testid="cpp-none-note">
          <Info size={16} className="mt-0.5 shrink-0 text-gold" />
          <span>Fast brainstorming mode — no project needed. Generate freely, then use the save bar afterward to create a project, attach to an existing one, download or copy.</span>
        </div>
      )}
    </div>
  );
}
